import { useQuery } from '@tanstack/react-query'
import { ChevronRight, CornerDownRight } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { MoneyText } from '@/components/ui/MoneyText'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { METHOD_LABELS } from '@/data/dues'
import { listSessionLines } from '@/data/income'
import type { IncomeSession, IncomeTransfer } from '@/data/income'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { cn } from '@/lib/cn'
import { ProofPhotos } from '@/features/proofs/ProofPhotos'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { reversalsBesideOriginals } from '@/lib/ledgerGroups'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** A person who counted: initials and name, as a small chip. */
function CounterChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-tint py-0.5 pl-0.5 pr-2.5 text-label font-medium text-neutral">
      <Avatar name={name} size="sm" className="h-5 w-5 text-micro" />
      {name}
    </span>
  )
}

/** The reason for a reversal, as a quoted muted line. */
function Reason({ text, on }: { text: string; on?: string }) {
  return (
    <p className="mt-0.5 text-label italic text-muted">
      Reason: “{text}”{on ? ` (${formatDay(on)})` : ''}
    </p>
  )
}

/** One counted service: a row you open to see the amounts, with who counted it and its count sheet. */
export function SessionRow({
  s,
  canWrite,
  expanded,
  onToggle,
  onReverse,
}: {
  s: IncomeSession
  canWrite: boolean
  expanded: boolean
  onToggle: () => void
  onReverse: (lineId: string, what: string) => void
}) {
  const lines = useQuery({ queryKey: ['income', 'lines', s.id], queryFn: () => listSessionLines(s.id), enabled: expanded })
  const ordered = reversalsBesideOriginals(lines.data ?? [], (l) => l.id, (l) => l.reversesId)
  return (
    <li className="py-4">
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onToggle} aria-expanded={expanded} className="group -ml-1 flex min-w-0 flex-1 items-start gap-2 rounded-control p-1 text-left hover:bg-page">
          <ChevronRight size={18} aria-hidden className={cn('mt-0.5 shrink-0 text-muted transition-transform', expanded && 'rotate-90')} />
          <span className="min-w-0">
            <span className="block text-card font-semibold text-ink">
              {formatDay(s.serviceOn)}
              {s.label ? ` · ${s.label}` : ''}
            </span>
            {/* One clean sentence for screen readers; the chips are the visual version of it. */}
            <span className="sr-only">Counted by {s.counter1} and {s.counter2}</span>
            <span aria-hidden className="mt-1 flex flex-wrap items-center gap-1.5 text-label text-muted">
              <span>Counted by</span>
              <CounterChip name={s.counter1} />
              <span>and</span>
              <CounterChip name={s.counter2} />
            </span>
            {s.note && <span className="mt-1 block text-body text-muted">{s.note}</span>}
            <span className="sr-only">{expanded ? 'Hide the amounts' : 'Show the amounts'}</span>
          </span>
        </button>
        <div className="shrink-0 text-right">
          <p className="text-card font-semibold tabular-nums">
            <MoneyText ngwee={s.totalNgwee} strongZero />
          </p>
          {s.photoCount === 0 ? <Badge tone="warning">No count sheet photo yet</Badge> : <Badge tone="success">Count sheet saved</Badge>}
        </div>
      </div>
      <div className="pl-6">
        <ProofPhotos parent={{ kind: 'incomeSession', id: s.id }} departmentId={null} date={s.serviceOn} canAdd={canWrite} noun="count sheet" />
        <OwnerDeleteButton table="income_session" id={s.id} what="counted service" className="mt-1" />
        {expanded &&
          (lines.isPending ? (
            <SkeletonRows rows={3} />
          ) : lines.isError ? (
            <p role="alert" className="mt-2 text-body text-bad">
              {errText(lines.error)}{' '}
              <Button variant="secondary" size="sm" onClick={() => void lines.refetch()}>
                Try again
              </Button>
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-control border border-line bg-surface" aria-label="Amounts counted">
              {ordered.map((l) => {
                const isReversal = !!l.reversesId
                return (
                  <li key={l.id} className={cn('flex flex-wrap items-center justify-between gap-2 px-3 py-2.5', isReversal && 'ml-6 border-l-2 border-l-bad/40 bg-bad-tint/50')}>
                    <div className="flex min-w-0 items-start gap-2">
                      {isReversal && <CornerDownRight size={16} aria-hidden className="mt-0.5 shrink-0 text-bad" />}
                      <div className="min-w-0">
                        <p className={cn('text-body font-medium', l.status === 'reversed' ? 'text-muted' : 'text-ink')}>
                          {l.fundName} · {METHOD_LABELS[l.method]}
                          {l.status === 'reversed' && <> <Badge tone="neutral">Reversed</Badge></>}
                          {isReversal && <> <Badge tone="danger">Reversal</Badge></>}
                        </p>
                        {l.reference && <p className="text-label text-muted">Reference {l.reference}</p>}
                        {l.reversalReason && <Reason text={l.reversalReason} on={l.effectiveOn} />}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn('text-body font-medium tabular-nums', l.status === 'reversed' && 'text-muted line-through')}>
                        <MoneyText ngwee={l.amountNgwee} strongZero />
                      </span>
                      {canWrite && l.status === 'posted' && !l.reversesId && (
                        <Button variant="secondary" size="sm" onClick={() => onReverse(l.id, `${formatKwacha(l.amountNgwee)} of ${l.fundName} (${METHOD_LABELS[l.method]})`)}>
                          Reverse
                        </Button>
                      )}
                      {!l.reversesId && <OwnerDeleteButton table="income_line" id={l.id} what="amount" />}
                    </div>
                  </li>
                )
              })}
            </ul>
          ))}
      </div>
    </li>
  )
}

/** Money sent on to the Conference or elsewhere. A reversal sits under the entry it cancels, linked to it. */
export function TransferRow({ t, canWrite, onReverse }: { t: IncomeTransfer; canWrite: boolean; onReverse: () => void }) {
  const isReversal = !!t.reversesId
  return (
    <li className={cn('py-4', isReversal && 'ml-6 -mt-1 border-l-2 border-l-bad/40 bg-bad-tint/50 pl-4 pr-3')}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          {isReversal && <CornerDownRight size={16} aria-hidden className="mt-1 shrink-0 text-bad" />}
          <div className="min-w-0">
            <p className={cn('text-body font-medium', t.status === 'reversed' ? 'text-muted' : 'text-ink')}>
              {formatDay(t.sentOn)} · {t.fundName} to {t.recipient}
              {t.status === 'reversed' && <> <Badge tone="neutral">Reversed</Badge></>}
              {isReversal && <> <Badge tone="danger">Reversal</Badge></>}
            </p>
            <p className="text-label text-muted">
              {METHOD_LABELS[t.method]}
              {t.reference ? ` · ${t.reference}` : ''}
            </p>
            {t.reversalReason && <Reason text={t.reversalReason} />}
            {t.note && !isReversal && <p className="text-label text-muted">{t.note}</p>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className={cn('text-body font-semibold tabular-nums', t.status === 'reversed' && 'text-muted line-through')}>
            <MoneyText ngwee={t.amountNgwee} strongZero />
          </p>
          {!isReversal && (t.photoCount === 0 ? <Badge tone="warning">No slip photo yet</Badge> : <Badge tone="success">Slip saved</Badge>)}
        </div>
      </div>
      {!isReversal && <ProofPhotos parent={{ kind: 'incomeTransfer', id: t.id }} departmentId={null} date={t.sentOn} canAdd={canWrite && t.status === 'posted'} noun="deposit slip" />}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {canWrite && t.status === 'posted' && !isReversal && (
          <Button variant="secondary" size="sm" onClick={onReverse}>
            Reverse
          </Button>
        )}
        {!isReversal && <OwnerDeleteButton table="income_transfer" id={t.id} what="transfer" />}
      </div>
    </li>
  )
}
